<script>
import OwnerApi from '@/api/owner';
import VendorApi from '@/api/vendor';
import ReservationApi from '@/api/reservation';
import _ from 'lodash';
import helper from '@/commons/helper';
import axios from "axios";
import useVuelidate from "@vuelidate/core";
import {required} from "@vuelidate/validators";

export default {
  name: 'Login',
  beforeRouteEnter(to, from, next) {
    const data = helper.Session.getLoginSettings();

    if (data.prefixCode) {
      let api = null;

      switch (data.portal) {
        case helper.Portals.OWNER:
          api = OwnerApi;
          break;
        case helper.Portals.VENDOR:
          api = VendorApi;
          break;
        case helper.Portals.RESERVATION:
          api = ReservationApi;
          break;
      }

      console.assert(api, 'Could not locate appropriate portal API');

      // Augment the LoginSettings with an API call
      api.getLoginSetting(data.prefixCode).then(response => {
        const settings = response.data.result;
        settings.prefixCode = data.prefixCode;
        settings.portal = data.portal;
        localStorage.setItem(helper.Session.LoginSettings, JSON.stringify(settings));
        next();
      }).catch(error => {
        console.log(error);
        next();
      });
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      portal: '',
      customerName: 'Resort Management System',
      backgroundUrl: '',
      userName: '',
      password: '',
      passwordRules: [],
      isLoading: false,
      loginFail: false
    };
  },
  validations() {
    return {
      userName: {
        required
      },
      password: {
        required
      }
    };
  },
  created() {
    const data = helper.Session.getLoginSettings();
    this.customerName = data.customerName;
    this.backgroundUrl = data.loginOptions.backgroundUrl;
    this.portal = data.portal;
  },
  methods: {
    hasRoute() {
      const check = _.find(this.$router.options.routes, {name: 'forgot-password'});
      return !!check;
    },
    uppercaseValue(e, prop) {
      const start = e.target.selectionStart;
      e.target.value = e.target.value.toUpperCase();
      this.$set(this, prop, e.target.value);
      e.target.setSelectionRange(start, start);
    },
    login(e) {
      // TODO: Clean this up!
      e.preventDefault();
      const settings = helper.Session.getLoginSettings();

      const data = {
        unitId: this.userName,
        vendorId: this.userName,
        reservationNumber: this.userName,
        lastName: this.password,
        password: this.password,
        prefixCode: settings.prefixCode,
        portal: settings.portal
      };

      let createSession;

      switch (data.portal) {
        case helper.Portals.OWNER: {
          createSession = OwnerApi.createSession(data.prefixCode, data.unitId, data.password);
          break;
        }
        case helper.Portals.VENDOR: {
          createSession = VendorApi.createSession(data.prefixCode, data.vendorId, data.password);
          break;
        }
        case helper.Portals.RESERVATION: {
          createSession = ReservationApi.createSession(data.prefixCode, data.reservationNumber, data.lastName);
          break;
        }
        default: {
          console.error('Could not determine portal configuration!');
        }
      }

      if (!this.v$.$invalid) {
        this.isLoading = true;
        createSession.then((response) => {
          if (response.data.success) {
            this.$cookies.set('token', encodeURIComponent(response.data.result), '48h', '/');
            switch (data.portal) {

              // Owner login
              case helper.Portals.OWNER: {
                const accountDetail = OwnerApi.getAccountDetail();
                const secretQuestion = OwnerApi.getSecretQuestion(data.prefixCode, data.unitId);
                axios.all([accountDetail, secretQuestion]).then(axios.spread((...responses) => {
                  const accountDetailResult = responses[0];
                  const secretQuestionResult = responses[1];
                  if (accountDetailResult.data.success) {
                    this.$store.dispatch('common/updateAccountInfo', accountDetailResult.data.result);

                    // Dispatch to password update if using stock password
                    if (secretQuestionResult.data.result === null) {
                      this.$router.push('account');
                    } else {
                      this.$router.push('calendar');
                    }
                  }
                })).catch(errors => {
                  console.log(errors);
                  this.loginFail = true;
                  this.isLoading = false;
                });
                break;
              }

              case helper.Portals.VENDOR: {
                VendorApi.getAccountDetail().then((response) => {
                  if (response.data.success) {
                    settings.vendorId = data.vendorId;
                    localStorage.setItem('loginSettings', JSON.stringify(settings));
                    this.$store.dispatch('common/updateAccountInfo', response.data.result);
                    this.$router.push('/');
                  }
                }, () => {
                  this.loginFail = true;
                  this.isLoading = false;
                });
                break;
              }

              case helper.Portals.RESERVATION: {
                ReservationApi.getUnitDetails().then((response) => {
                  settings.reservationNumber = this.userName;
                  settings.lastName = this.password;
                  localStorage.setItem('loginSettings', JSON.stringify(settings));
                  this.$store.dispatch('common/updateUnitDetails', response.data.result);
                  ReservationApi.getReservationDetails().then((response) => {
                    settings.reservationNumber = this.userName;
                    settings.lastName = this.password;
                    localStorage.setItem('loginSettings', JSON.stringify(settings));
                    this.$store.dispatch('common/updateReservationDetails', response.data.result);
                    this.$router.push('/');
                  }, () => {
                    this.loginFail = true;
                    this.isLoading = false;
                  });
                }, () => {
                  this.loginFail = true;
                  this.isLoading = false;
                });

                break;
              }
            }
          } else {
            this.loginFail = true;
            this.isLoading = false;
          }
        }, () => {
          this.loginFail = true;
          this.isLoading = false;
        });
      } else {
        this.v$.$touch();
      }
    }
  }
};
</script>

<template>
  <div :style="{ background: 'url(' + backgroundUrl + ') center no-repeat' }" class="login-container">
    <div
      v-if="$helper.isMobile()"
      v-show="loginFail || v$.userName.$error || v$.password.$error"
      class="mb-login-warning">
      <span v-if="loginFail">Invalid ID or password</span>
      <span v-if="v$.userName.$error || v$.password.$error">Field is required</span>
    </div>
    <form role="form" @submit.stop.prevent="login($event)">
      <div class="header mb-xl">
        <h4 class="company-name mb">
          {{customerName.toUpperCase()}}
        </h4>
        <p v-if="portal==='owner'" class="portal-type">
          {{portal | capitalize}} Portal Login
        </p>
        <p v-if="portal==='vendor'" class="portal-type">
          Cleaning {{portal | capitalize}} Portal Login
        </p>
        <p v-if="portal==='reservation'" class="portal-type">
          Lookup {{portal | capitalize}}
        </p>
      </div>
      <div
        class="form-group bb"
        :class="{'border-red': v$.userName.$error, 'border-red-mb': v$.userName.$error && $helper.isMobile()}">
        <div class="input-group align-items-center">
          <div v-if="!$helper.isMobile()" class="img flex align-items-center">
            <img src="/img/user.png" alt="User Icon">
          </div>

          <input
            v-if="portal==='vendor'"
            v-model="userName"
            name="user-name"
            class="form-control"
            type="text"
            :placeholder="portal + ' ID' | capitalize"
            @input="uppercaseValue($event, 'userName')">

          <input
            v-if="portal==='owner'"
            v-model="userName"
            name="user-name"
            class="form-control"
            type="text"
            placeholder="Unit ID"
            @input="uppercaseValue($event, 'userName')">

          <input
            v-if="portal==='reservation'"
            v-model="userName"
            name="user-name"
            class="form-control"
            type="text"
            placeholder="Reservation Number">

          <div v-if="$helper.isMobile()" class="mb-element">
            <div class="mb-background" />
            <div class="img">
              <img src="/img/user.png" alt="User Icon">
            </div>
          </div>
        </div>
      </div>

      <div
        class="form-group bb"
        :class="{'border-red': v$.password.$error, 'border-red-mb': v$.password.$error && $helper.isMobile()}">
        <div class="input-group align-items-center">
          <div v-if="!$helper.isMobile()" class="img flex align-items-center">
            <img src="/img/pass.png" alt="Password">
          </div>

          <input
            v-model="password"
            class="form-control"
            :type="portal === 'reservation' ? 'text' : 'password'"
            :placeholder="portal === 'reservation' ? 'Reservation Last Name' : 'Password'"
            name="password">

          <div v-if="$helper.isMobile()" class="mb-element">
            <div v-if="$helper.isMobile()" class="mb-background" />
            <div class="img">
              <img src="/img/pass.png" alt="Password">
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!$helper.isMobile()"
        v-show="loginFail || v$.userName.$error || v$.password.$error"
        class="invalid-warning">
        <span v-if="loginFail">
          {{portal === 'reservation' ? 'Reservation not found' : 'Invalid ID or password'}}
        </span>
        <span v-if="v$.userName.$error || v$.password.$error">Field is required</span>
      </div>
      <div class="form-group pt-lg">
        <button v-show="!isLoading" class="btn btn-primary w-100 login-button btn-block" type="submit">
          <span class="vertical-middle">{{portal === 'reservation' ? 'LOOKUP' : 'LOGIN'}}</span>
        </button>
        <div v-show="isLoading" class="login-button flex justify-content-center align-items-center">
          <div class="ball-spin-fade-loader">
            <!-- TODO: Fix loader by finding a better way to render it than this -->
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
          </div>
          <div class="text-uppercase ml-xl">
            <span class="vertical-middle">Loading</span>
          </div>
        </div>
        <div v-if="hasRoute()" class="text-center pt-sm">
          <router-link :to="{name: 'forgot-password'}">
            Forgot Password
          </router-link>
        </div>
      </div>
      <div class="footer text-center">
        <span>Powered by<a href="https://resortmanagementsystem.com"> RMS</a></span>
      </div>
    </form>
  </div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@mixin placeholder {
  ::-webkit-input-placeholder {
    @content
  }
  :-moz-placeholder {
    @content
  }
  ::-moz-placeholder {
    @content
  }
  :-ms-input-placeholder {
    @content
  }
}

.login-container {
  /* background: url('../assets/images/bg.png') center no-repeat; */
  background-color: gray !important;
  background-size: cover;
  position: relative;
  width: 100%;
  height: 100vh;

  .header {
    width: 100%;
    color: #244b8d;
    text-align: center;
    font-family: AvenirLTStd Medium;

    .company-name {
      font-size: 24px;
      line-height: 36px;
    }
  }

  .footer {
    font-size: 13px;
    font-family: AvenirLTStd Medium;
    color: #68727d;
  }

  .bb {
    border-color: #e6eaed;
  }

  .border-red {
    border-color: #f00c17;
  }

  .border-red-mb {
    border: 1px solid #f00c17 !important;
    border-radius: 20px !important;
  }

  .img {
    img {
      width: auto;
      margin: auto;
    }
  }

  form {
    @include placeholder {
      color: #68727d;
      font-family: AvenirLTStd Roman;
    }

    width: 550px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 50px 50px 25px;
    background-color: #fff;
    border-radius: 10px;
    font-size: 15px;
    font-family: AvenirLTStd Roman;

    input {
      display: inline-block;
      width: 95%;
      box-shadow: none;
      border: none;
      color: #32383f;
      font-family: AvenirLTStd Roman;
    }

    .login-button {
      background-color: #244b8d;
      border: none;
      font-size: 15px;
      font-family: AvenirLTStd Medium;
      line-height: 18px;
      vertical-align: middle;
      padding-top: 10px;
      padding-bottom: 10px;
      box-shadow: none;
      border-radius: 5px;
      color: #fff;

      &:hover {
        background-color: #3e5d92;
        box-shadow: none;
      }
    }

    button.login-button {
      &:hover, &:active, &:focus {
        background-color: #3e5d92;
        box-shadow: none;
      }
    }

    .form-group {
      margin-bottom: 30px;

      a, a:hover, a:visited {
        font-size: 13px;
      }
    }
  }

  @media screen and (max-width: 823px) {
    form {
      @include placeholder {
        color: #32383f;
      }
      background-color: #fff;
      width: calc(100vw - 10px);
      color: #32383f;
      padding: 25px;

      .header {
        color: #244b8d;
      }

      .footer {
        color: #68727d;
      }

      .input-group {
        input {
          width: 100%;
          color: #32383f;
          background-color: transparent;
          height: 40px;
          line-height: 50px;
          padding-left: 60px;
          z-index: 2;
          font-family: AvenirLTStd Medium;
        }

        .mb-element {
          .mb-background {
            position: absolute;
            width: 100%;
            height: 100%;
            background-color: #68727d;
            opacity: 0.25;
            top: 0;
            left: 0;
            border-radius: 50px;
            z-index: 1;
          }

          .img {
            position: absolute;
            top: 12px;
            left: 23px;
            z-index: 2;

            img {
              max-width: 75%;
            }
          }
        }
      }

      .form-group {
        border: none;
      }

      .login-button {
        height: 40px;
        border-radius: 50px;
      }
    }
    .mb-login-warning {
      width: calc(100vw - 60px);
      max-width: 350px;
      background-color: #fff;
      color: #f00c17;
      margin: auto;
      padding: 15px;
      font-family: AvenirLTStd Roman;
      z-index: 3;
    }
  }
}
</style>
